<template>
  <div class="after-sales-page">
    <!-- Banner Section -->
    <div class="banner-section">
      <div class="banner-content">
        <h1 class="banner-title">{{ bannerTitle }}</h1>
      </div>
    </div>

    <!-- Intelligent Service Section -->
    <div class="service-section">
      <div class="section-container">
        <h2 class="section-title">{{ serviceTitle }}</h2>
        <div class="service-list">
          <div class="service-item"
               v-for="(item, index) in serviceItems"
               :key="index">
            <div class="item-image-wrapper">
              <img :src="item.image"
                   :alt="item.title"
                   class="item-image" />
            </div>
            <div class="item-content">
              <h3 class="item-title">{{ item.title }}</h3>
              <p class="item-desc">{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Operation Videos Section -->
    <div class="video-section">
      <div class="section-container">
        <h2 class="section-title">{{ videosTitle }}</h2>
        <div class="grid-list">
          <div class="video-item"
               v-for="(item, index) in visibleVideos"
               :key="index">
            <div class="video-cover-wrapper">
              <img :src="item.cover"
                   :alt="item.title"
                   class="video-cover" />
            </div>
            <div class="item-bottom-content">
              <h3 class="bottom-title">{{ item.title }}</h3>
              <button class="action-btn"
                      @click="openUrl(item.url)">
                <span class="btn-icon">▶</span>
                <span class="btn-text">{{ playButtonText }}</span>
              </button>
            </div>
          </div>
        </div>
        <!-- Pagination -->
        <div class="pagination"
             v-if="videoPages > 1">
          <button v-for="p in videoPages"
                  :key="p"
                  :class="['page-number', { active: currentVideoPage === p }]"
                  @click="currentVideoPage = p">{{ p }}</button>
        </div>
      </div>
    </div>

    <!-- Operation Manuals Section -->
    <div class="manual-section">
      <div class="section-container">
        <h2 class="section-title">{{ manualsTitle }}</h2>
        <div class="grid-list">
          <div class="manual-item"
               v-for="(item, index) in visibleManuals"
               :key="index">
            <div class="manual-cover-wrapper">
              <img :src="item.cover"
                   :alt="item.title"
                   class="manual-cover" />
            </div>
            <div class="item-bottom-content">
              <h3 class="bottom-title">{{ item.title }}</h3>
              <button class="action-btn"
                      @click="openUrl(item.url)">
                <span class="btn-icon">📄</span>
                <span class="btn-text">{{ viewPdfButtonText }}</span>
              </button>
            </div>
          </div>
        </div>
        <!-- Pagination -->
        <div class="pagination"
             v-if="manualPages > 1">
          <button v-for="p in manualPages"
                  :key="p"
                  :class="['page-number', { active: currentManualPage === p }]"
                  @click="currentManualPage = p">{{ p }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { afterSalesApi } from '@/api'

export default {
  name: 'AfterSalesService',
  data() {
    return {
      afterSalesLoaded: false,
      afterSalesItems: [],
      serviceAssets: [
        { image: require('@/assets/after-sales/serve1.png') },
        { image: require('@/assets/after-sales/serve2.png') },
        { image: require('@/assets/after-sales/serve3.png') },
        { image: require('@/assets/after-sales/serve4.png') }
      ],
      currentVideoPage: 1,
      itemsPerPage: 4,
      videoAssets: [
        { cover: 'https://images.unsplash.com/photo-1581093458791-9f3c3900df4b?auto=format&fit=crop&w=600&q=80' },
        { cover: 'https://images.unsplash.com/photo-1581093458791-9f3c3900df4b?auto=format&fit=crop&w=600&q=80' },
        { cover: 'https://images.unsplash.com/photo-1581093458791-9f3c3900df4b?auto=format&fit=crop&w=600&q=80' },
        { cover: 'https://images.unsplash.com/photo-1581093458791-9f3c3900df4b?auto=format&fit=crop&w=600&q=80' },
        { cover: 'https://images.unsplash.com/photo-1581093458791-9f3c3900df4b?auto=format&fit=crop&w=600&q=80' },
        { cover: 'https://images.unsplash.com/photo-1581093458791-9f3c3900df4b?auto=format&fit=crop&w=600&q=80' }
      ],
      currentManualPage: 1,
      manualAssets: [
        { cover: 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?auto=format&fit=crop&w=600&q=80' },
        { cover: 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?auto=format&fit=crop&w=600&q=80' },
        { cover: 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?auto=format&fit=crop&w=600&q=80' },
        { cover: 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?auto=format&fit=crop&w=600&q=80' },
        { cover: 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?auto=format&fit=crop&w=600&q=80' }
      ]
    }
  },
  mounted() {
    this.loadAfterSales()
  },
  computed: {
    afterSalesCopy() {
      const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'zh-CN'
      const messages =
        this.$i18n && this.$i18n.getLocaleMessage
          ? this.$i18n.getLocaleMessage(locale)
          : {}
      return (messages.support && messages.support.afterSales) || {}
    },
    bannerTitle() {
      return this.afterSalesCopy.bannerTitle || ''
    },
    serviceTitle() {
      return this.afterSalesCopy.serviceTitle || ''
    },
    serviceItems() {
      const list = this.afterSalesCopy.serviceItems || []
      return list.map((item, index) => {
        const asset = this.serviceAssets[index] || {}
        return {
          title: item.title || '',
          desc: item.desc || '',
          image: asset.image || ''
        }
      })
    },
    videosTitle() {
      return this.afterSalesCopy.videosTitle || ''
    },
    playButtonText() {
      return this.afterSalesCopy.playButtonText || ''
    },
    manualsTitle() {
      return this.afterSalesCopy.manualsTitle || ''
    },
    viewPdfButtonText() {
      return this.afterSalesCopy.viewPdfButtonText || ''
    },
    manuals() {
      if (this.afterSalesLoaded) {
        const list = this.afterSalesItems
          .filter((item) => item && item.category === '文件')
          .slice()
          .sort((a, b) => {
            const sa = Number.isFinite(Number(a.sortOrder)) ? Number(a.sortOrder) : 0
            const sb = Number.isFinite(Number(b.sortOrder)) ? Number(b.sortOrder) : 0
            if (sa !== sb) return sa - sb
            const ta = a.createTime ? new Date(a.createTime).getTime() : 0
            const tb = b.createTime ? new Date(b.createTime).getTime() : 0
            return tb - ta
          })
        return list.map((item) => ({
          id: item.id,
          title: item.name || '',
          cover: this.normalizeUrl(item.imageUrl),
          url: this.normalizeUrl(item.fileUrl),
          sortOrder: item.sortOrder,
        }))
      }

      const list = this.afterSalesCopy.manuals || []
      return list.map((item, index) => {
        const asset = this.manualAssets[index] || {}
        return {
          title: item.title || '',
          cover: asset.cover || ''
        }
      })
    },
    videos() {
      if (this.afterSalesLoaded) {
        const list = this.afterSalesItems
          .filter((item) => item && item.category === '视频')
          .slice()
          .sort((a, b) => {
            const sa = Number.isFinite(Number(a.sortOrder)) ? Number(a.sortOrder) : 0
            const sb = Number.isFinite(Number(b.sortOrder)) ? Number(b.sortOrder) : 0
            if (sa !== sb) return sa - sb
            const ta = a.createTime ? new Date(a.createTime).getTime() : 0
            const tb = b.createTime ? new Date(b.createTime).getTime() : 0
            return tb - ta
          })
        return list.map((item) => ({
          id: item.id,
          title: item.name || '',
          cover: this.normalizeUrl(item.imageUrl),
          url: this.normalizeUrl(item.fileUrl),
          sortOrder: item.sortOrder,
        }))
      }

      const list = this.afterSalesCopy.videos || []
      return list.map((item, index) => {
        const asset = this.videoAssets[index] || {}
        return {
          title: item.title || '',
          cover: asset.cover || ''
        }
      })
    },
    videoPages() {
      return Math.ceil(this.videos.length / this.itemsPerPage);
    },
    visibleVideos() {
      const start = (this.currentVideoPage - 1) * this.itemsPerPage;
      return this.videos.slice(start, start + this.itemsPerPage);
    },
    manualPages() {
      return Math.ceil(this.manuals.length / this.itemsPerPage);
    },
    visibleManuals() {
      const start = (this.currentManualPage - 1) * this.itemsPerPage;
      return this.manuals.slice(start, start + this.itemsPerPage);
    }
  },
  methods: {
    normalizeUrl(url) {
      if (!url) return ''
      const str = String(url).trim()
      if (!str) return ''
      const noBackticks = str.replace(/`/g, '').trim()
      return noBackticks.replace(/^["']+|["']+$/g, '').trim()
    },
    openUrl(url) {
      const cleaned = this.normalizeUrl(url)
      if (!cleaned) return
      window.open(cleaned, '_blank')
    },
    async loadAfterSales() {
      try {
        const result = await afterSalesApi.selectAll()
        const ok =
          result &&
          (result.code === 200 ||
            result.success === true ||
            result.ok === true)
        const list = ok && Array.isArray(result.data) ? result.data : []
        const enabled = list.filter((item) => item && item.state === 0)
        this.afterSalesItems = enabled
        this.afterSalesLoaded = true
        this.currentVideoPage = 1
        this.currentManualPage = 1
      } catch (e) {
        this.afterSalesLoaded = false
        this.afterSalesItems = []
      }
    },
  },
}
</script>

<style scoped>
.after-sales-page {
  width: 100%;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  padding-bottom: 80px;
}

/* Banner Section */
.banner-section {
  width: 100%;
  height: 680px;
  background-image: url("@/assets/after-sales/banner.png");
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.banner-content {
  text-align: center;
}

.banner-title {
  font-size: 48px;
  font-weight: bold;
  color: #fff;
  margin: 0;
}

/* Intelligent Service Section */
.service-section {
  width: 100%;
  height: 682px;
  background-color: #f4f6fa;
  padding-top: 80px;
  box-sizing: border-box;
}

.section-container {
  width: 1200px;
  margin: 0 auto;
  text-align: center;
}

.section-title {
  font-size: 34px;
  color: #333;
  font-weight: bold;
  margin-bottom: 60px;
}

.service-list {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.service-item {
  width: 272px;
  height: 396px;
  border-radius: 12px;
  overflow: hidden;

  display: flex;
  flex-direction: column;
}

.item-image-wrapper {
  width: 272px;
  height: 196px;
}

.item-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-content {
  width: 100%;
  height: 200px;
  background-color: #fff;
  padding: 30px 20px;
  box-sizing: border-box;
  text-align: center;
}

.item-title {
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 15px;
}

.item-desc {
  font-size: 16px;
  color: #333;
  line-height: 1.5;
  text-align: center;
}

/* Common Styles for Video & Manual Sections */
.video-section,
.manual-section {
  width: 100%;
  padding-top: 80px;
  background-color: #fff;
}

.grid-list {
  display: grid;
  grid-template-columns: repeat(
    2,
    1fr
  ); /* 2 items per row to fit 522px items in 1200px container */
  gap: 80px 156px; /* Vertical gap 80px, Horizontal gap calculated: (1200 - 522*2) = 156px */
  margin-bottom: 60px;
}

/* Video Items */
.video-item {
  width: 522px;
  height: 463px;
  display: flex;
  flex-direction: column;
}

.video-cover-wrapper {
  width: 522px;
  height: 294px;
}

.video-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Manual Items */
.manual-item {
  width: 522px;
  height: 630px;
  display: flex;
  flex-direction: column;
}

.manual-cover-wrapper {
  width: 522px;
  height: 461px;
  box-sizing: border-box;
  border: 2px solid #f2f2f2;
}

.manual-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Common Bottom Content */
.item-bottom-content {
  width: 522px;
  height: 169px;
  background-color: #f2f2f2;
  padding: 30px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center; /* Center button horizontally */
}

.bottom-title {
  width: 100%;
  font-size: 20px;
  font-weight: bold;
  color: #333;
  text-align: left; /* Title left aligned */
  margin: 0 0 20px 0;
}

.action-btn {
  width: 160px; /* Adjust width as needed for content */
  height: 44px; /* Adjust height as needed, though padding usually handles it. User said "inside icon+text", let's use padding */
  padding: 8px 24px;
  background-color: #2ea2fa;
  border-radius: 72px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.3s;
}

.action-btn:hover {
  background-color: #1a8ee6;
}

.btn-icon {
  font-size: 18px; /* Approx 28x28 container effect */
  color: #fff;
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
}

.btn-text {
  font-size: 16px;
  color: #fff;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  margin-top: 40px;
  gap: 15px;
}

.page-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #fff;
  border: 1px solid #ddd;
  color: #666;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.page-number:hover {
  border-color: #2ea2fa;
  color: #2ea2fa;
}

.page-number.active {
  background-color: #2ea2fa;
  border-color: #2ea2fa;
  color: #fff;
  transform: none;
}
</style>
